import { useState, useEffect } from "react";

/* PACKAGES */
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import * as yup from "yup";
import { useFormik } from "formik";
import Select from "react-select";
import { State } from "country-state-city";
import Confetti from "react-confetti";
import { motion } from "framer-motion";

/* MATERIAL TAILWIND COMPONENTS */
import {
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  Radio,
} from "@material-tailwind/react";

/* USER DEFINED COMPONENTS */
import {
  CouponCodeField,
  FormInput,
  PhoneInputField,
} from "../../components/core/input";
import { FormButton } from "../../components/core/buttons";

/* ICONS */
import {
  MdChevronLeft,
  MdChevronRight,
  MdEmail,
  MdPhone,
} from "react-icons/md";
import { FiEdit } from "react-icons/fi";
import { FaCircleCheck, FaRegCircleCheck } from "react-icons/fa6";
import { AiOutlineExclamationCircle } from "react-icons/ai";
import { IoClose, IoAddCircle } from "react-icons/io5";

/* DATA */
import savedAddresses from "../../data/savedAddresses";

/* CONTAINER ANIMATION */
const containerAnimation = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 1 },
  },
};

/* DIV ANIMATION */
const animation = {
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      damping: 12,
      delay: 1,
      stiffness: 100,
    },
  },
  hidden: {
    opacity: 0,
    y: 20,
    transition: {
      type: "spring",
      damping: 12,
      delay: 1,
      stiffness: 100,
    },
  },
};

/* VALIDATION SCHEMA */
const validation = yup.object().shape({
  firstName: yup
    .string()
    .min(2, "Too short")
    .required("First name is required"),
  lastName: yup.string().min(2, "Too short").required("Last name is required"),
  email: yup.string().email().required("Last name is required"),
  phone: yup.string().min(2, "Too short").required("Phone number is required"),
  addressLine1: yup
    .string()
    .min(2, "Too short")
    .required("Address line 1 is required"),
  city: yup.string().min(2, "Too short").required("City is required"),
  state: yup.string().required("State is required"),
  zipCode: yup.string().min(2, "Too short").required("Pincode is required"),
  country: yup.string().required("Country is required"),
});

/* COUPON CODE VALIDATION SCHEMA */
const couponValidation = yup.object().shape({
  couponCode: yup
    .string()
    .min(2, "Too short")
    .required("Coupon code is required"),
});

/* ADD ADDRESS DIALOG */
const addressValidation = yup.object().shape({
  firstName: yup
    .string()
    .min(2, "Too short")
    .required("First name is required"),
  lastName: yup.string().min(2, "Too short").required("Last name is required"),
  addressLine1: yup
    .string()
    .min(2, "Too short")
    .required("Address line 1 is required"),
  city: yup.string().min(2, "Too short").required("City is required"),
  state: yup.string().required("State is required"),
  zipCode: yup.string().min(2, "Too short").required("Pincode is required"),
  country: yup.string().required("Country is required"),
});

/* SHIPPING METHODS */
const shippingMethods = [
  {
    id: 1,
    name: "shippingMethods",
    label: "Ground",
    deliveryDays: "2-3 days",
    price: "50.00",
  },
  {
    id: 2,
    name: "shippingMethods",
    label: "3-day select",
    deliveryDays: "upto 3 days",
    price: "90.00",
  },
  {
    id: 3,
    name: "shippingMethods",
    label: "Next-day",
    deliveryDays: "1 day",
    price: "110.00",
  },
  {
    id: 4,
    name: "shippingMethods",
    label: "Express critical",
    deliveryDays: "by the end of day",
    price: "150.00",
  },
];

const Checkout = () => {
  /* STATES */
  const [step, setStep] = useState("Shipping");
  const [loading, setLoading] = useState(false);
  const [addressSelected, setAddressSelected] = useState(1);
  const [states, setStates] = useState([]);
  const [billingAddress, setBillingAddress] = useState("No");
  const [billingDialog, setBillingDialog] = useState(false);
  const [addressDialog, setAddressDialog] = useState(false);
  const [couponDialog, setCouponDialog] = useState(false);

  /* SHIPPING INFO FORM HANDLER */
  const shippingInfo = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      zipCode: "",
      country: "India",
      couponCode: "",
    },
    validationSchema: validation,
    onSubmit: (values) => {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
      }, 3000);
      console.log("Submitted values: ", values);
    },
  });

  /* BILLING INFO FORM HANDLER */
  const billingInfo = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      zipCode: "",
      country: "India",
    },
    validationSchema: validation,
    onSubmit: (values) => {
      console.log("Submitted values: ", values);
    },
  });

  /* COUPON CODE HANDLER */
  const couponCodeInfo = useFormik({
    initialValues: {
      couponCode: "FIRST10",
    },
    validationSchema: couponValidation,
    onSubmit: (values, { resetForm }) => {
      console.log("Coupon code: ", values);
    },
  });

  /* ADD NEW ADDRESS FORM HANDLER */
  const newAddressInfo = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      zipCode: "",
      country: "India",
    },
    validationSchema: addressValidation,
    onSubmit: (values) => {
      console.log("Submitted values: ", values);
    },
  });

  /* ADDRESS CARD */
  const AddresscCard = (props) => {
    return (
      <div
        className={`w-addressBox h-full bg-white border ${
          props.id === addressSelected
            ? "border-primaryRed"
            : "border-greyBorder"
        } hover:border-primaryRed hover:border-opacity-50 shadow-md rounded-lg p-5 cursor-pointer transition-all duration-300 ease-in-out relative`}
        onClick={props.onClick}
      >
        <h5 className="text-lg font-source font-bold text-black">
          {props.name}
        </h5>
        <div className="mt-1">
          <p className="text-sm font-source font-normal text-headerGrey">
            {props.building}
          </p>
          <p className="text-sm font-source font-normal text-headerGrey">
            {props.nearbyPlace}
          </p>
          <p className="text-sm font-source font-normal text-headerGrey">
            {props.streetLine}
          </p>
          <p className="text-sm font-source font-normal text-headerGrey">
            {props.city}, {props.state}
          </p>
        </div>

        {/* SELECTED CARD */}
        {props.id === addressSelected && (
          <div className="absolute top-2 right-2">
            <FaCircleCheck className="w-5 h-5 text-primaryRed" />
          </div>
        )}
      </div>
    );
  };

  /* SHIPPING DETAILS */
  const ShippingDetailsBox = (props) => {
    return (
      <div className="w-full bg-white rounded-lg border border-greyBorder p-5">
        <h5 className="text-lg font-fraunces-bold tracking-tight">
          Shipping Details
        </h5>
        <p className="text-sm font-source font-normal text-headerGrey">
          Your entered shipping details
        </p>
        <div className="mt-5">
          <h5 className="text-lg font-source font-bold text-black">
            {props.name}
          </h5>
          <div className="mt-1">
            <p className="text-sm font-source font-normal text-headerGrey">
              {props.building}
            </p>
            <p className="text-sm font-source font-normal text-headerGrey">
              {props.street}
            </p>
            <p className="text-sm font-source font-normal text-headerGrey">
              {props.city}, {props.state}
            </p>
          </div>
          <div className="mt-2">
            <p className="text-sm font-source font-normal text-headerGrey flex items-center gap-2 leading-loose">
              <span className="font-source font-semibold text-headerGrey">
                <MdEmail className="w-4 h-4" />
              </span>{" "}
              {props.email}
            </p>
            <p className="text-sm font-source font-normal text-headerGrey flex items-center gap-2">
              <span className="font-source font-semibold text-headerGrey">
                <MdPhone className="w-4 h-4" />
              </span>{" "}
              {props.phone}
            </p>
          </div>
        </div>
      </div>
    );
  };

  /* BILLING DETAILS */
  const BillingDetailsBox = (props) => {
    return (
      <div className="w-full h-full bg-white border border-greyBorder rounded-lg p-5">
        <div className="flex items-center justify-between">
          <div>
            <h5 className="text-lg font-fraunces-bold tracking-tight">
              Billing Details
            </h5>
            <p className="text-sm font-source font-normal text-headerGrey">
              Please enter your billing details
            </p>
          </div>
          {billingAddress === "Yes" && (
            <Button
              className="bg-white hover:bg-black hover:bg-opacity-5 border border-headerGrey rounded-full p-2 shadow-none hover:shadow-none transition-all duration-300 ease-in-out"
              onClick={() => setBillingDialog(!billingDialog)}
            >
              <FiEdit className="w-4 h-4 text-headerGrey" />
            </Button>
          )}
        </div>
        <div className="mt-0">
          {billingAddress === "No" ? (
            <div className="w-4/5 mx-auto mt-10">
              <p className="text-base font-source font-normal text-headerGrey text-center">
                Are the billing details same as shipping details?
              </p>
              <div className="flex items-center justify-center gap-2 mt-3">
                <Button
                  className="shadow-none hover:shadow-none bg-white hover:bg-primaryRed hover:bg-opacity-15 border border-greyBorder hover:border-primaryRed hover:border-opacity-20 text-sm font-source font-semibold text-black px-3 py-1 rounded-full normal-case"
                  onClick={() => setBillingAddress("Yes")}
                >
                  Yes
                </Button>
                <Button
                  className="shadow-none hover:shadow-none bg-white hover:bg-primaryRed hover:bg-opacity-15 border border-greyBorder hover:border-primaryRed hover:border-opacity-20 text-sm font-source font-semibold text-black px-3 py-1 rounded-full normal-case"
                  onClick={() => setBillingDialog(!billingDialog)}
                >
                  No
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-5">
              <h5 className="text-lg font-source font-bold text-black">
                {props.name}
              </h5>
              <div className="mt-1">
                <p className="text-sm font-source font-normal text-headerGrey">
                  {props.building}
                </p>
                <p className="text-sm font-source font-normal text-headerGrey">
                  {props.street}
                </p>
                <p className="text-sm font-source font-normal text-headerGrey">
                  {props.city}, {props.state}
                </p>
              </div>
              <div className="mt-2">
                <p className="text-sm font-source font-normal text-headerGrey flex items-center gap-2 leading-loose">
                  <span className="font-source font-semibold text-headerGrey">
                    <MdEmail className="w-4 h-4" />
                  </span>{" "}
                  {props.email}
                </p>
                <p className="text-sm font-source font-normal text-headerGrey flex items-center gap-2">
                  <span className="font-source font-semibold text-headerGrey">
                    <MdPhone className="w-4 h-4" />
                  </span>{" "}
                  {props.phone}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  /* SHIPPING METHOD BOX */
  const ShippingMethodBox = (props) => {
    return (
      <div className="w-full bg-white rounded-lg border border-greyBorder radio-btn p-3">
        <Radio
          name={props.name}
          className="p-0"
          labelProps={{
            className: "w-full",
          }}
          label={
            <div className="w-full ml-1">
              <div className="w-full">
                <h5 className="text-lg font-source font-semibold text-black tracking-tight">
                  {props.label}{" "}
                  <span className="text-primaryRed font-source font-bold">
                    (${props.price})
                  </span>
                </h5>
                <p className="text-sm font-source font-normal text-headerGrey">
                  Delivery Time: {props.deliveryDays}
                </p>
              </div>
            </div>
          }
        ></Radio>
      </div>
    );
  };

  /* CART SUMMARY TILE */
  const ProductSummaryTile = (props) => {
    return (
      <div className="flex items-center gap-5">
        <div className="w-1/4 flex-shrink-0 relative">
          <div className="bg-white border border-greyBorder rounded-lg flex items-center justify-center py-2">
            <img
              src={props.image}
              className="w-4/5 h-auto object-cover"
              alt="Product name"
            />
          </div>
          <div className="absolute -top-2 -right-2 bg-primaryRed w-6 h-6 rounded-full flex items-center justify-center">
            <p className="text-sm font-source font-semibold text-white">
              {props.quantity ?? 1}
            </p>
          </div>
        </div>
        <div className="w-3/4">
          <h5 className="text-lg font-source font-bold text-black">
            {props.name}
          </h5>

          <p className="text-lg font-source font-semibold text-black tracking-tight mt-1">
            ${props.price}
          </p>
        </div>
      </div>
    );
  };

  /* ORDER SUMMARY TILE */
  const OrderSummaryTile = (props) => {
    return (
      <div className="flex items-center justify-between first:mt-0 mt-2">
        <h5 className="text-base font-source font-normal text-headerGrey">
          {props.label}
        </h5>
        <h5 className="text-lg font-source font-bold text-black tracking-tight">
          ${props.value}
        </h5>
      </div>
    );
  };

  /* CONFIRMATION TILE */
  const ConfirmationTile = (props) => {
    return (
      <div className="grid grid-cols-4 mt-2 first:mt-0">
        <div className="col-span-1">
          <p className="text-base font-source font-semibold text-black">
            {props.label}
          </p>
        </div>
        <div className="col-span-3">
          <p className="text-base font-source font-normal text-headerGrey">
            {props.value}
          </p>
        </div>
      </div>
    );
  };

  /* USE EFFECT */
  useEffect(() => {
    let tempArr = [];
    State.getStatesOfCountry("IN").forEach((data) => {
      tempArr.push({ label: data.name, value: data.name });
    });
    setStates(tempArr);
  }, []);

  const [billingToggle, setBillingToggle] = useState(true);

  return (
    <motion.section
      variants={containerAnimation}
      initial="hidden"
      animate="visible"
      className="bg-greyBg bg-opacity-40 py-6 xl:py-14 lg:py-14"
    >
      <div className="container">
        <motion.div variants={animation} className="mb-6 xl:mb-10 lg:mb-10">
          <p className="text-sm font-source uppercase tracking-[0.3em] text-headerGrey">
            Secure Checkout
          </p>
          <h1 className="mt-3 text-3xl xl:text-4xl lg:text-4xl font-fraunces-bold tracking-tight text-black">
            Review your order and ship with priority.
          </h1>
          <p className="mt-2 text-base xl:text-lg lg:text-lg font-source text-headerGrey max-w-3xl">
            Confirm addresses, pick delivery speed, and keep your wholesale
            inventory moving.
          </p>
        </motion.div>

        {/* PAYMENT AND SHIPPING DETAILS */}
        {(step === "Payment" || step === "Shipping") && (
          <motion.div
            variants={animation}
            className="grid grid-cols-1 xl:grid-cols-3 lg:grid-cols-3 gap-5 mt-5"
          >
            <div className="col-span-2">
              <div className="w-full bg-white border border-greyBorder rounded-2xl p-6 shadow-sm">
                {/* SHIPPING INFO */}
                <div className={`${step === "Shipping" ? "block" : "hidden"}`}>
                  {/* SAVED ADDRESSES */}
                  <div className="block">
                    {/* HEADING */}
                    <div className="flex items-center justify-between">
                      <div className="w-2/3 xl:w-full lg:w-full">
                        <h2 className="text-xl font-fraunces-bold tracking-tight text-black">
                          Your saved addresses
                        </h2>
                        <p className="text-sm font-source font-normal text-headerGrey">
                          Your saved addresses for seamless shopping experience.
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button className="shadow-none hover:shadow-none p-1 rounded-full bg-white border border-greyBorder prev-button">
                          <MdChevronLeft className="w-5 h-5 text-black" />
                        </Button>
                        <Button className="shadow-none hover:shadow-none p-1 rounded-full bg-white border border-greyBorder next-button">
                          <MdChevronRight className="w-5 h-5 text-black" />
                        </Button>
                      </div>
                    </div>

                    {/* ADDRESS CARD */}
                    <div className="mt-5">
                      <Swiper
                        modules={[Navigation]}
                        navigation={{
                          nextEl: ".next-button",
                          prevEl: ".prev-button",
                        }}
                        slidesPerView={2}
                        spaceBetween={15}
                        className="pb-2"
                      >
                        {savedAddresses.map((address) => (
                          <SwiperSlide className="!w-auto" key={address.id}>
                            <AddresscCard
                              id={address.id}
                              name={address.name}
                              building={address.building}
                              streetLine={address.streetLine}
                              city={address.city}
                              state={address.state}
                              onClick={() => setAddressSelected(address.id)}
                            />
                          </SwiperSlide>
                        ))}
                        <SwiperSlide
                          className="!w-auto min-h-[14.5vh]"
                          onClick={() => setAddressDialog(!addressDialog)}
                        >
                          <div className="w-addressBox min-h-[14.5vh] bg-white border border-greyBorder hover:border-primaryRed hover:border-opacity-50 shadow-md rounded-lg p-5 cursor-pointer transition-all duration-300 ease-in-out">
                            <div className="h-[10vh] flex flex-col items-center justify-center">
                              <IoAddCircle className="w-7 h-7 text-headerGrey" />
                              <h5 className="mt-1 text-base font-source font-normal text-headerGrey text-center tracking-tight">
                                Add address
                              </h5>
                            </div>
                          </div>
                        </SwiperSlide>
                      </Swiper>
                    </div>
                  </div>

                  {/* SHIPPING INFORMATION */}
                  <div className="mt-6 xl:mt-10 lg:mt-10">
                    <h2 className="text-xl font-fraunces-bold tracking-tight text-black">
                      Your shipping information
                    </h2>
                    <p className="text-sm font-source font-normal text-headerGrey">
                      Enter your shipping details for hassle-free shopping.
                    </p>
                    <form className="mt-5" onSubmit={shippingInfo.handleSubmit}>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                          <FormInput
                            type="text"
                            name="firstName"
                            id="firstName"
                            placeholder="First name"
                            value={shippingInfo.values.firstName}
                            onChange={shippingInfo.handleChange}
                            errorHandler={
                              shippingInfo.errors.firstName &&
                              shippingInfo.touched.firstName
                            }
                          />
                        </div>
                        <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                          <FormInput
                            type="text"
                            name="lastName"
                            id="lastName"
                            placeholder="Last name"
                            value={shippingInfo.values.lastName}
                            onChange={shippingInfo.handleChange}
                            errorHandler={
                              shippingInfo.errors.lastName &&
                              shippingInfo.touched.lastName
                            }
                          />
                        </div>
                        <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                          <FormInput
                            type="email"
                            name="email"
                            id="email"
                            placeholder="Email address"
                            value={shippingInfo.values.email}
                            onChange={shippingInfo.handleChange}
                            errorHandler={
                              shippingInfo.errors.email &&
                              shippingInfo.touched.email
                            }
                          />
                        </div>
                        <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                          <PhoneInputField
                            value={shippingInfo.values.phone}
                            onChange={shippingInfo.handleChange}
                            errorHandler={
                              shippingInfo.errors.phone &&
                              shippingInfo.touched.phone
                            }
                          />
                        </div>
                        <div className="col-span-2">
                          <FormInput
                            type="text"
                            name="addressLine1"
                            id="addressLine1"
                            placeholder="Address Line 1"
                            value={shippingInfo.values.addressLine1}
                            onChange={shippingInfo.handleChange}
                            errorHandler={
                              shippingInfo.errors.addressLine1 &&
                              shippingInfo.touched.addressLine1
                            }
                          />
                        </div>
                        <div className="col-span-2">
                          <FormInput
                            type="text"
                            name="addressLine2"
                            id="addressLine2"
                            placeholder="Address Line 2 (optional)"
                            value={shippingInfo.values.addressLine2}
                            onChange={shippingInfo.handleChange}
                            errorHandler={
                              shippingInfo.errors.addressLine2 &&
                              shippingInfo.touched.addressLine2
                            }
                          />
                        </div>
                        <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                          <FormInput
                            type="text"
                            name="city"
                            id="city"
                            placeholder="City"
                            value={shippingInfo.values.city}
                            onChange={shippingInfo.handleChange}
                            errorHandler={
                              shippingInfo.errors.city &&
                              shippingInfo.touched.city
                            }
                          />
                        </div>
                        <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                          <div className="relative">
                            <Select
                              options={states}
                              placeholder="State"
                              className={`${
                                shippingInfo.errors.state && "error"
                              }`}
                              classNamePrefix="state"
                              name="state"
                              id="state"
                              value={shippingInfo.values.state}
                              onChange={(e) => {
                                shippingInfo.setFieldValue("state", e);
                              }}
                            />

                            {shippingInfo.errors.state &&
                              shippingInfo.touched.state && (
                                <div className="absolute top-3 right-8">
                                  <AiOutlineExclamationCircle className="w-5 h-5 text-red-500" />
                                </div>
                              )}

                            {shippingInfo.values.state !== "" && (
                              <div className="absolute top-3 right-8">
                                <FaRegCircleCheck className="w-5 h-5 text-green-500" />
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                          <FormInput
                            type="number"
                            name="zipCode"
                            id="zipCode"
                            placeholder="Zipcode"
                            value={shippingInfo.values.zipCode}
                            onChange={shippingInfo.handleChange}
                            errorHandler={
                              shippingInfo.errors.zipCode &&
                              shippingInfo.touched.zipCode
                            }
                          />
                        </div>
                        <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                          <FormInput
                            type="text"
                            name="country"
                            id="country"
                            placeholder="Country"
                            value={shippingInfo.values.country}
                            onChange={shippingInfo.handleChange}
                            errorHandler={
                              shippingInfo.errors.country &&
                              shippingInfo.touched.country
                            }
                          />
                        </div>
                      </div>
                    </form>
                  </div>

                  {/* SHIPPING METHOD */}
                  <div className="mt-6 xl:mt-10 lg:mt-10">
                    {/* HEADING */}
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-fraunces-bold tracking-tight text-black">
                          Shipping methods
                        </h2>
                        <p className="text-sm font-source font-normal text-headerGrey">
                          Select the shipping method according to your
                          convenience
                        </p>
                      </div>
                    </div>

                    <form className="mt-5">
                      <div className="grid grid-cols-1 xl:grid-cols-2 lg:grid-cols-2 gap-5">
                        {shippingMethods.map((data) => (
                          <div className="col-span-1" key={data.id}>
                            <ShippingMethodBox
                              name={data.name}
                              label={data.label}
                              deliveryDays={data.deliveryDays}
                              price={data.price}
                            />
                          </div>
                        ))}
                      </div>
                    </form>

                    {/* BILLING ADDRESS */}
                    <div className="mt-6">
                      <div className="w-full bg-white rounded-lg border border-greyBorder radio-btn p-3">
                        <Checkbox
                          name="billingAddress"
                          className="p-0"
                          value={billingToggle}
                          onChange={() => setBillingToggle(!billingToggle)}
                          labelProps={{
                            className: "w-full",
                          }}
                          label={
                            <div className="w-full ml-1">
                              <div className="w-full">
                                <h5 className="text-lg font-source font-semibold text-black tracking-tight">
                                  Use billing address same as shipping address
                                </h5>
                              </div>
                            </div>
                          }
                        ></Checkbox>
                      </div>
                    </div>

                    {!billingToggle && (
                      <div className="mt-6 xl:mt-10 lg:mt-10">
                        <h2 className="text-xl font-fraunces-bold tracking-tight text-black">
                          Your billing information
                        </h2>
                        <p className="text-sm font-source font-normal text-headerGrey">
                          Enter your shipping details for hassle-free shopping.
                        </p>
                        <form
                          className="mt-5"
                          onSubmit={shippingInfo.handleSubmit}
                        >
                          <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                              <FormInput
                                type="text"
                                name="firstName"
                                id="firstName"
                                placeholder="First name"
                                value={shippingInfo.values.firstName}
                                onChange={shippingInfo.handleChange}
                                errorHandler={
                                  shippingInfo.errors.firstName &&
                                  shippingInfo.touched.firstName
                                }
                              />
                            </div>
                            <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                              <FormInput
                                type="text"
                                name="lastName"
                                id="lastName"
                                placeholder="Last name"
                                value={shippingInfo.values.lastName}
                                onChange={shippingInfo.handleChange}
                                errorHandler={
                                  shippingInfo.errors.lastName &&
                                  shippingInfo.touched.lastName
                                }
                              />
                            </div>
                            <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                              <FormInput
                                type="email"
                                name="email"
                                id="email"
                                placeholder="Email address"
                                value={shippingInfo.values.email}
                                onChange={shippingInfo.handleChange}
                                errorHandler={
                                  shippingInfo.errors.email &&
                                  shippingInfo.touched.email
                                }
                              />
                            </div>
                            <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                              <PhoneInputField
                                value={shippingInfo.values.phone}
                                onChange={shippingInfo.handleChange}
                                errorHandler={
                                  shippingInfo.errors.phone &&
                                  shippingInfo.touched.phone
                                }
                              />
                            </div>
                            <div className="col-span-2">
                              <FormInput
                                type="text"
                                name="addressLine1"
                                id="addressLine1"
                                placeholder="Address Line 1"
                                value={shippingInfo.values.addressLine1}
                                onChange={shippingInfo.handleChange}
                                errorHandler={
                                  shippingInfo.errors.addressLine1 &&
                                  shippingInfo.touched.addressLine1
                                }
                              />
                            </div>
                            <div className="col-span-2">
                              <FormInput
                                type="text"
                                name="addressLine2"
                                id="addressLine2"
                                placeholder="Address Line 2 (optional)"
                                value={shippingInfo.values.addressLine2}
                                onChange={shippingInfo.handleChange}
                                errorHandler={
                                  shippingInfo.errors.addressLine2 &&
                                  shippingInfo.touched.addressLine2
                                }
                              />
                            </div>
                            <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                              <FormInput
                                type="text"
                                name="city"
                                id="city"
                                placeholder="City"
                                value={shippingInfo.values.city}
                                onChange={shippingInfo.handleChange}
                                errorHandler={
                                  shippingInfo.errors.city &&
                                  shippingInfo.touched.city
                                }
                              />
                            </div>
                            <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                              <div className="relative">
                                <Select
                                  options={states}
                                  placeholder="State"
                                  className={`${
                                    shippingInfo.errors.state && "error"
                                  }`}
                                  classNamePrefix="state"
                                  name="state"
                                  id="state"
                                  value={shippingInfo.values.state}
                                  onChange={(e) => {
                                    shippingInfo.setFieldValue("state", e);
                                  }}
                                />

                                {shippingInfo.errors.state &&
                                  shippingInfo.touched.state && (
                                    <div className="absolute top-3 right-8">
                                      <AiOutlineExclamationCircle className="w-5 h-5 text-red-500" />
                                    </div>
                                  )}

                                {shippingInfo.values.state !== "" && (
                                  <div className="absolute top-3 right-8">
                                    <FaRegCircleCheck className="w-5 h-5 text-green-500" />
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                              <FormInput
                                type="number"
                                name="zipCode"
                                id="zipCode"
                                placeholder="Zipcode"
                                value={shippingInfo.values.zipCode}
                                onChange={shippingInfo.handleChange}
                                errorHandler={
                                  shippingInfo.errors.zipCode &&
                                  shippingInfo.touched.zipCode
                                }
                              />
                            </div>
                            <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                              <FormInput
                                type="text"
                                name="country"
                                id="country"
                                placeholder="Country"
                                value={shippingInfo.values.country}
                                onChange={shippingInfo.handleChange}
                                errorHandler={
                                  shippingInfo.errors.country &&
                                  shippingInfo.touched.country
                                }
                              />
                            </div>
                          </div>
                        </form>
                      </div>
                    )}

                    <div className="mt-6 xl:mt-10 lg:mt-10 flex items-center gap-2">
                      <Button
                        className="shadow-none hover:shadow-none bg-white border border-greyBorder rounded-full text-black text-sm font-source font-semibold normal-case py-3 leading-none"
                        onClick={() => setStep("Shipping")}
                      >
                        Go back
                      </Button>
                      <FormButton
                        type="button"
                        label="Proceed with payment"
                        onClick={() => setStep("Confirmation")}
                      />
                    </div>
                  </div>
                </div>
                {/* PAYMENT INFORMATION */}
                <div className={`${step === "Payment" ? "block" : "hidden"}`}>
                  <div className="grid grid-cols-1 xl:grid-cols-2 lg:grid-cols-2 gap-5">
                    {/* SHIPPING DETAILS */}
                    <div className="col-span-1">
                      <ShippingDetailsBox
                        name="Darsh Shah"
                        building="G-24 Gokuldham Shopping Center"
                        street="Film city road, Goregaon (E)"
                        city="Mumbai"
                        state="Maharashtra"
                        email="darsh@roundtechsquare.com"
                        phone="+91 9574094129"
                      />
                    </div>

                    {/* BILLING DETAILS */}
                    <div className="col-span-1">
                      <BillingDetailsBox
                        name="Darsh Shah"
                        building="G-24 Gokuldham Shopping Center"
                        street="Film city road, Goregaon (E)"
                        city="Mumbai"
                        state="Maharashtra"
                        email="darsh@roundtechsquare.com"
                        phone="+91 9574094129"
                      />
                    </div>
                  </div>

                  {/* SHIPPING METHOD */}
                  <div className="mt-6 xl:mt-10 lg:mt-10">
                    {/* HEADING */}
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-fraunces-bold tracking-tight text-black">
                          Shipping methods
                        </h2>
                        <p className="text-sm font-source font-normal text-headerGrey">
                          Select the shipping method according to your
                          convenience
                        </p>
                      </div>
                    </div>

                    <form className="mt-5">
                      <div className="grid grid-cols-1 xl:grid-cols-2 lg:grid-cols-2 gap-5">
                        {shippingMethods.map((data) => (
                          <div className="col-span-1" key={data.id}>
                            <ShippingMethodBox
                              name={data.name}
                              label={data.label}
                              deliveryDays={data.deliveryDays}
                              price={data.price}
                            />
                          </div>
                        ))}
                      </div>
                      <div className="mt-6 xl:mt-10 lg:mt-10 flex items-center gap-2">
                        <Button
                          className="shadow-none hover:shadow-none bg-white border border-greyBorder rounded-full text-black text-sm font-source font-semibold normal-case py-3 leading-none"
                          onClick={() => setStep("Shipping")}
                        >
                          Go back
                        </Button>
                        <FormButton
                          type="button"
                          label="Proceed with payment"
                          onClick={() => setStep("Confirmation")}
                        />
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-span-1 h-auto xl:h-fit lg:fit relative xl:sticky lg:sticky top-5">
              <div className="bg-white border border-greyBorder rounded-2xl p-5 shadow-sm">
                {/* PROMO CODES */}
                <div>
                  <h2 className="text-xl font-fraunces-bold tracking-tight text-black">
                    Coupon codes
                  </h2>
                  <p className="text-sm font-source font-normal text-headerGrey">
                    Apply promocodes for exclusive discounts and deals.
                  </p>
                  <div className="mt-5">
                    <form onSubmit={couponCodeInfo.handleSubmit}>
                      <div className="flex items-center gap-2">
                        <div className="w-3/4">
                          <CouponCodeField
                            type="text"
                            name="couponCode"
                            id="couponCode"
                            placeholder="Coupon code"
                            value={couponCodeInfo.values.couponCode}
                            onChange={couponCodeInfo.handleChange}
                            errorHandler={
                              couponCodeInfo.errors.couponCode &&
                              couponCodeInfo.touched.couponCode
                            }
                          />
                        </div>
                        <div className="w-1/4">
                          <Button
                            className="bg-primaryRed hover:bg-opacity-80 shadow-none hover:shadow-none text-sm font-source font-semibold rounded-full normal-case"
                            onClick={() => setCouponDialog(!couponDialog)}
                          >
                            Apply
                          </Button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>

                {/* ORDER SUMMARY */}
                <div className="mt-6 pt-6 border-t border-greyBorder">
                  <h2 className="text-xl font-fraunces-bold tracking-tight text-black">
                    Order Summary
                  </h2>
                  <p className="text-sm font-source font-normal text-headerGrey">
                    Review your order total along with necessary details
                  </p>
                  <div className="mt-5">
                    <div className="bg-greyBg bg-opacity-60 border border-greyBorder rounded-xl p-5">
                      <OrderSummaryTile label="Subtotal" value="1151.00" />
                      <OrderSummaryTile label="Shipping" value="10.00" />
                      <OrderSummaryTile label="Taxes" value="40.00" />
                      <div className="mt-3 border-t border-[#E5E5E5]">
                        <div className="flex items-center justify-between pt-3">
                          <h5 className="text-lg font-source font-semibold text-headerGrey">
                            Total
                          </h5>
                          <h5 className="text-xl font-source font-bold text-black tracking-tight">
                            $1201.00
                          </h5>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CART SUMMARY */}
                <div className="mt-6 pt-6 border-t border-greyBorder">
                  <h2 className="text-xl font-fraunces-bold tracking-tight text-black">
                    Cart Summary
                  </h2>
                  <p className="text-sm font-source font-normal text-headerGrey">
                    Review you selection: A recap of your shopping triumph
                  </p>
                  <div className="mt-5">
                    <div className="w-full bg-greyBg bg-opacity-60 p-5 rounded-xl border border-greyBorder">
                      <ProductSummaryTile
                        image="https://d1pd8tx4g8m009.cloudfront.net/product-images/Black-Beige-Stylish-Typography-Business-Tips-Pinterest-Pin-3-17553651615852025-08-16-17-26-01nROd1.png"
                        name="Taiwan Ocean Bomb 315ml Sparkling Water"
                        price="1,151.00"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ORDER CONFIRMATION */}
        {step === "Confirmation" && (
          <motion.div
            variants={animation}
            className="w-full xl:w-5/6 lg:w-5/6 mx-auto"
          >
            <div className="grid grid-cols-1 xl:grid-cols-2 lg:grid-cols-2 gap-5 xl:gap-20 lg:gap-20 mt-5 xl:mt-10 lg:mt-10 items-center">
              <div className="col-span-1">
                <h2 className="text-3xl xl:text-4xl lg:text-4xl font-source font-bold text-black leading-tight tracking-tight">
                  Thank you for your purchase with Food Farmacy!
                </h2>
                <p className="mt-5 text-base font-source font-normal text-headerGrey">
                  Your order will be processed within 24 hours during working
                  days. We will notify you by email once your order has been
                  shipped.
                </p>
                <div className="mt-5">
                  <h5 className="text-xl font-source font-semibold text-black">
                    Billing Address
                  </h5>
                  <div className="mt-3">
                    <ConfirmationTile label="Name" value="Darsh Shah" />
                    <ConfirmationTile
                      label="Address"
                      value="FF-6 Ankur Appartment, Nr. Umiyanagar Duplex, New Sama Road, Vadodara, Gujarat"
                    />
                    <ConfirmationTile label="Phone" value="+91 9574094812" />
                    <ConfirmationTile
                      label="Email"
                      value="darsh@roundtechsquare.com"
                    />
                  </div>
                </div>
              </div>
              <div className="col-span-1">
                <div className="w-full bg-greyBg bg-opacity-80 rounded-xl p-4 xl:p-8 lg:p-8">
                  <h5 className="text-2xl font-source font-bold text-black tracking-tight">
                    Order Summary
                  </h5>
                  <div className="mt-3 border-t border-b border-[#E5E5E5] py-3">
                    <div className="grid grid-cols-3 gap-5">
                      <div className="col-span-1 border-r border-greyBorder">
                        <p className="text-sm font-source font-normal text-headerGrey">
                          Date
                        </p>
                        <h5 className="text-base font-source font-bold text-black">
                          05 March, 2024
                        </h5>
                      </div>
                      <div className="col-span-1 border-r border-greyBorder">
                        <p className="text-sm font-source font-normal text-headerGrey">
                          Order Number
                        </p>
                        <h5 className="text-base font-source font-bold text-black">
                          024-125478956
                        </h5>
                      </div>
                      <div className="col-span-1">
                        <p className="text-sm font-source font-normal text-headerGrey">
                          Payment Method
                        </p>
                        <h5 className="text-base font-source font-bold text-black">
                          Mastercard
                        </h5>
                      </div>
                    </div>
                  </div>
                  <div className="border-b border-greyBorder py-5">
                    <ProductSummaryTile
                      image={require("../../assets/images/products/02.png")}
                      name="Real Plant Powered Protein Shake"
                      company="Evolve"
                      price="650.00"
                      quantity="5"
                    />
                  </div>
                  <div className="py-5">
                    <OrderSummaryTile label="Subtotal" value="600.00" />
                    <OrderSummaryTile label="Shipping" value="10.00" />
                    <OrderSummaryTile label="Taxes" value="40.00" />
                    <div className="mt-3 border-t border-[#E5E5E5]">
                      <div className="flex items-center justify-between pt-3">
                        <h5 className="text-lg font-source font-bold text-black">
                          Order Total
                        </h5>
                        <h5 className="text-xl font-source font-bold text-black tracking-tight">
                          ₹650.00
                        </h5>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* BILLING ADDRESS DIALOG */}
      <Dialog
        open={billingDialog}
        handler={() => setBillingDialog(!billingDialog)}
        size="sm"
        dismiss={{
          enabled: false,
        }}
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.9, y: -100 },
        }}
        className="!min-w-[90%] xl:!min-w-max lg:!min-w-max"
      >
        <DialogBody className="p-0">
          <div className="border-b border-greyBorder p-5 flex items-center justify-between">
            <h5 className="text-lg font-source font-semibold text-black">
              Add Billing Details
            </h5>
            <Button
              className="p-0 bg-white shadow-none hover:shadow-none text-headerGrey"
              onClick={() => setBillingDialog(!billingDialog)}
            >
              <IoClose className="w-5 h-5" />
            </Button>
          </div>
          <form className="p-0" onSubmit={billingInfo.handleSubmit}>
            <div className="grid grid-cols-2 gap-3 p-5">
              <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                <FormInput
                  type="text"
                  name="firstName"
                  id="firstName"
                  placeholder="First name"
                  value={billingInfo.values.firstName}
                  onChange={billingInfo.handleChange}
                  errorHandler={
                    billingInfo.errors.firstName &&
                    billingInfo.touched.firstName
                  }
                />
              </div>
              <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                <FormInput
                  type="text"
                  name="lastName"
                  id="lastName"
                  placeholder="Last name"
                  value={billingInfo.values.lastName}
                  onChange={billingInfo.handleChange}
                  errorHandler={
                    billingInfo.errors.lastName && billingInfo.touched.lastName
                  }
                />
              </div>
              <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                <FormInput
                  type="email"
                  name="email"
                  id="email"
                  placeholder="Email address"
                  value={billingInfo.values.email}
                  onChange={billingInfo.handleChange}
                  errorHandler={
                    billingInfo.errors.email && billingInfo.touched.email
                  }
                />
              </div>
              <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                <PhoneInputField
                  value={billingInfo.values.phone}
                  onChange={billingInfo.handleChange}
                  errorHandler={
                    billingInfo.errors.phone && billingInfo.touched.phone
                  }
                />
              </div>
              <div className="col-span-2">
                <FormInput
                  type="text"
                  name="addressLine1"
                  id="addressLine1"
                  placeholder="Address Line 1"
                  value={billingInfo.values.addressLine1}
                  onChange={billingInfo.handleChange}
                  errorHandler={
                    billingInfo.errors.addressLine1 &&
                    billingInfo.touched.addressLine1
                  }
                />
              </div>
              <div className="col-span-2">
                <FormInput
                  type="text"
                  name="addressLine2"
                  id="addressLine2"
                  placeholder="Address Line 2 (optional)"
                  value={billingInfo.values.addressLine2}
                  onChange={billingInfo.handleChange}
                  errorHandler={
                    billingInfo.errors.addressLine2 &&
                    billingInfo.touched.addressLine2
                  }
                />
              </div>
              <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                <FormInput
                  type="text"
                  name="city"
                  id="city"
                  placeholder="City"
                  value={billingInfo.values.city}
                  onChange={billingInfo.handleChange}
                  errorHandler={
                    billingInfo.errors.city && billingInfo.touched.city
                  }
                />
              </div>
              <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                <div className="relative">
                  <Select
                    options={states}
                    placeholder="State"
                    className={`${billingInfo.errors.state && "error"}`}
                    classNamePrefix="state"
                    name="state"
                    id="state"
                    value={billingInfo.values.state}
                    onChange={(e) => {
                      billingInfo.setFieldValue("state", e);
                    }}
                  />

                  {billingInfo.errors.state && billingInfo.touched.state && (
                    <div className="absolute top-3 right-8">
                      <AiOutlineExclamationCircle className="w-5 h-5 text-red-500" />
                    </div>
                  )}

                  {billingInfo.values.state !== "" && (
                    <div className="absolute top-3 right-8">
                      <FaRegCircleCheck className="w-5 h-5 text-green-500" />
                    </div>
                  )}
                </div>
              </div>
              <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                <FormInput
                  type="number"
                  name="zipCode"
                  id="zipCode"
                  placeholder="Zipcode"
                  value={billingInfo.values.zipCode}
                  onChange={billingInfo.handleChange}
                  errorHandler={
                    billingInfo.errors.zipCode && billingInfo.touched.zipCode
                  }
                />
              </div>
              <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                <FormInput
                  type="text"
                  name="country"
                  id="country"
                  placeholder="Country"
                  value={billingInfo.values.country}
                  onChange={billingInfo.handleChange}
                  errorHandler={
                    billingInfo.errors.country && billingInfo.touched.country
                  }
                />
              </div>
            </div>

            <div className="border-t border-greyBorder p-5 flex items-center justify-end">
              <FormButton
                type="submit"
                label="Submit details"
                loading={loading}
              />
            </div>
          </form>
        </DialogBody>
      </Dialog>

      {/* ADD NEW ADDRESS DIALOG */}
      <Dialog
        open={addressDialog}
        handler={() => setAddressDialog(!addressDialog)}
        size="sm"
        dismiss={{
          enabled: false,
        }}
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.9, y: -100 },
        }}
        className="!min-w-[90%] xl:!min-w-max lg:!min-w-max"
      >
        <DialogBody className="p-0">
          <div className="border-b border-greyBorder p-5 flex items-center justify-between">
            <h5 className="text-lg font-source font-semibold text-black">
              Add Address
            </h5>
            <Button
              className="p-0 bg-white shadow-none hover:shadow-none text-headerGrey"
              onClick={() => setAddressDialog(!addressDialog)}
            >
              <IoClose className="w-5 h-5" />
            </Button>
          </div>

          <form className="p-0" onSubmit={newAddressInfo.handleSubmit}>
            <div className="grid grid-cols-2 gap-3 p-5">
              <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                <FormInput
                  type="text"
                  name="firstName"
                  id="firstName"
                  placeholder="First name"
                  value={newAddressInfo.values.firstName}
                  onChange={newAddressInfo.handleChange}
                  errorHandler={
                    newAddressInfo.errors.firstName &&
                    newAddressInfo.touched.firstName
                  }
                />
              </div>
              <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                <FormInput
                  type="text"
                  name="lastName"
                  id="lastName"
                  placeholder="Last name"
                  value={newAddressInfo.values.lastName}
                  onChange={newAddressInfo.handleChange}
                  errorHandler={
                    newAddressInfo.errors.lastName &&
                    newAddressInfo.touched.lastName
                  }
                />
              </div>
              <div className="col-span-2">
                <FormInput
                  type="text"
                  name="addressLine1"
                  id="addressLine1"
                  placeholder="Address Line 1"
                  value={newAddressInfo.values.addressLine1}
                  onChange={newAddressInfo.handleChange}
                  errorHandler={
                    newAddressInfo.errors.addressLine1 &&
                    newAddressInfo.touched.addressLine1
                  }
                />
              </div>
              <div className="col-span-2">
                <FormInput
                  type="text"
                  name="addressLine2"
                  id="addressLine2"
                  placeholder="Address Line 2 (optional)"
                  value={newAddressInfo.values.addressLine2}
                  onChange={newAddressInfo.handleChange}
                  errorHandler={
                    newAddressInfo.errors.addressLine2 &&
                    newAddressInfo.touched.addressLine2
                  }
                />
              </div>
              <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                <FormInput
                  type="text"
                  name="city"
                  id="city"
                  placeholder="City"
                  value={newAddressInfo.values.city}
                  onChange={newAddressInfo.handleChange}
                  errorHandler={
                    newAddressInfo.errors.city && newAddressInfo.touched.city
                  }
                />
              </div>
              <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                <div className="relative">
                  <Select
                    options={states}
                    placeholder="State"
                    className={`${newAddressInfo.errors.state && "error"}`}
                    classNamePrefix="state"
                    name="state"
                    id="state"
                    value={newAddressInfo.values.state}
                    onChange={(e) => {
                      newAddressInfo.setFieldValue("state", e);
                    }}
                  />

                  {newAddressInfo.errors.state &&
                    newAddressInfo.touched.state && (
                      <div className="absolute top-3 right-8">
                        <AiOutlineExclamationCircle className="w-5 h-5 text-red-500" />
                      </div>
                    )}

                  {newAddressInfo.values.state !== "" && (
                    <div className="absolute top-3 right-8">
                      <FaRegCircleCheck className="w-5 h-5 text-green-500" />
                    </div>
                  )}
                </div>
              </div>
              <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                <FormInput
                  type="number"
                  name="zipCode"
                  id="zipCode"
                  placeholder="Zipcode"
                  value={newAddressInfo.values.zipCode}
                  onChange={newAddressInfo.handleChange}
                  errorHandler={
                    newAddressInfo.errors.zipCode &&
                    newAddressInfo.touched.zipCode
                  }
                />
              </div>
              <div className="col-span-2 xl:col-span-1 lg:col-span-1">
                <FormInput
                  type="text"
                  name="country"
                  id="country"
                  placeholder="Country"
                  value={newAddressInfo.values.country}
                  onChange={newAddressInfo.handleChange}
                  errorHandler={
                    newAddressInfo.errors.country &&
                    newAddressInfo.touched.country
                  }
                />
              </div>
            </div>

            <div className="border-t border-greyBorder p-5 flex items-center justify-end">
              <FormButton
                type="submit"
                label="Submit details"
                loading={loading}
              />
            </div>
          </form>
        </DialogBody>
      </Dialog>

      {/* COUPON CODE SUCCESSFUL */}
      <Dialog
        open={couponDialog}
        handler={() => setCouponDialog(!couponDialog)}
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.9, y: -100 },
        }}
        className="xl:!min-w-[40vh] xl:!max-w-[40vh] lg:!min-w-[40vh] lg:!max-w-[40vh] !min-w-[90%] !max-w-[90%]"
      >
        <DialogBody className="px-5 py-10 relative">
          <Confetti className="w-full h-full" />
          <div className="flex flex-col justify-center items-center">
            <img
              src={require("../../assets/images/icons/check.png")}
              className="w-20 h-20"
              alt="Coupon code applied successfully"
            />
            <div className="mt-5 text-center">
              <h5 className="text-lg font-source font-semibold text-black">
                "FIRST10" applied
              </h5>
              <h4 className="text-2xl font-source font-bold text-black">
                You saved ₹60.00
              </h4>
              <p className="text-base font-source font-normal text-black">
                with this coupon code.
              </p>
            </div>
          </div>

          {/* CLOSE BUTTON */}
          <div className="absolute top-3 right-3">
            <Button
              className="p-0 bg-white shadow-none hover:shadow-none text-headerGrey"
              onClick={() => setCouponDialog(!couponDialog)}
            >
              <IoClose className="w-4 h-4" />
            </Button>
          </div>
        </DialogBody>
      </Dialog>
    </motion.section>
  );
};

export default Checkout;
